import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, GraduationCap, RotateCcw } from "lucide-react";
import Seo from "../components/Seo";

const PROVINCES = [
  { code: "ON", name: "Ontario" },
  { code: "QC", name: "Québec" },
  { code: "BC", name: "British Columbia" },
  { code: "AB", name: "Alberta" },
  { code: "MB", name: "Manitoba" },
];

export default function CbtPractice() {
  const [province, setProvince] = useState("ON");
  const [quiz, setQuiz] = useState(null);
  const availableProvinces = useMemo(
    () => PROVINCES.filter(p => (quiz?.available || []).includes(p.code)), [quiz]);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const load = (p) => {
    setError(""); setResult(null); setAnswers({});
    api.get(`/cbt/quiz?province=${p}`).then(r => setQuiz(r.data))
      .catch(e => { setQuiz(null); setError(e.response?.data?.detail || "Could not load quiz"); });
  };
  useEffect(() => { load(province); }, [province]);

  const submit = async () => {
    try {
      const { data } = await api.post("/cbt/submit", { province, answers });
      setResult(data);
    } catch (e) { setError(e.response?.data?.detail || "Submit failed"); }
  };

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 text-center" data-testid="cbt-locked">
        <GraduationCap className="w-12 h-12 mx-auto text-muted-foreground" />
        <h2 className="font-display text-2xl font-semibold mt-4">CBT practice locked</h2>
        <p className="text-muted-foreground text-sm mt-2 max-w-md mx-auto">{error}</p>
        <Button className="rounded-sm mt-6" onClick={() => window.location.href = "/trainers"} data-testid="cbt-find-trainer">Find a trainer</Button>
      </div>
    );
  }

  if (!quiz) return <div className="max-w-3xl mx-auto p-12 text-muted-foreground">Loading quiz…</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 space-y-6" data-testid="cbt-page">
      <Seo title="CBT / Theory Practice – DriveTerrain" description="Practice provincial CBT / theory questions before your road test." canonical="/cbt" robots="noindex, nofollow" hrefLangs={false} />
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-primary">CBT · Theory Practice</div>
        <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight mt-1">{quiz.title}</h1>
        <div className="flex flex-wrap gap-2 mt-4">
          {availableProvinces.map(p => (
            <button key={p.code} onClick={() => setProvince(p.code)}
              className={`px-3 py-1.5 text-xs uppercase tracking-widest border rounded-sm ${province === p.code ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}
              data-testid={`cbt-prov-${p.code}`}>{p.name}</button>
          ))}
        </div>
      </div>

      {!result ? (
        <>
          <div className="space-y-4">
            {quiz.questions.map((q, i) => (
              /* Keyed on question text, not index: switching province swaps the
                 whole bank, and index keys would let the previous province's
                 option buttons be reused in place. */
              <div key={q.q} className="border border-border bg-card p-5" data-testid={`cbt-q-${i}`}>
                <div className="text-sm font-medium">{i + 1}. {q.q}</div>
                <div className="grid sm:grid-cols-2 gap-2 mt-3">
                  {q.options.map((opt, j) => (
                    <button key={opt} type="button" onClick={() => setAnswers(a => ({ ...a, [i]: j }))}
                      className={`text-left text-sm px-3 py-2.5 border rounded-sm ${answers[i] === j ? "border-primary bg-primary/10" : "border-border"}`}
                      data-testid={`cbt-q${i}-opt${j}`}>{opt}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <Button className="rounded-sm h-11 px-6" onClick={submit} disabled={Object.keys(answers).length < quiz.questions.length} data-testid="cbt-submit">Submit answers</Button>
        </>
      ) : (
        <div className="border border-border bg-card p-6 space-y-4" data-testid="cbt-result">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Your score</div>
              <div className="font-display text-5xl font-semibold mt-1" data-testid="cbt-score">{result.score}%</div>
              <div className="text-sm text-muted-foreground mt-1">{result.correct} / {result.total} correct</div>
            </div>
            <Button variant="outline" className="rounded-sm" onClick={() => load(province)} data-testid="cbt-retry"><RotateCcw className="w-4 h-4 mr-2" />Retake</Button>
          </div>
          <div className="space-y-1.5 mt-4">
            {result.detail.map((d, i) => (
              <div key={d.index ?? i} className="flex items-start gap-2 text-sm">
                {d.ok ? <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />}
                <div>
                  <div className="font-medium">{quiz.questions[i].q}</div>
                  {!d.ok && <div className="text-xs text-muted-foreground">Correct answer: <span className="text-foreground font-medium">{quiz.questions[i].options[d.correct_index]}</span></div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
